import { Link, useLocation } from "react-router-dom";
import "./NavMenu.scss";
import { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box/Box";
import List from "@mui/material/List/List";
import ListItem from "@mui/material/ListItem/ListItem";
import ListItemButton from "@mui/material/ListItemButton/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon/ListItemIcon";
import ListItemText from "@mui/material/ListItemText/ListItemText";
import AppBar from "@mui/material/AppBar/AppBar";
import Toolbar from "@mui/material/Toolbar/Toolbar";
import IconButton from "@mui/material/IconButton/IconButton";
import Typography from "@mui/material/Typography/Typography";
import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import Drawer from "@mui/material/Drawer/Drawer";
import NavMenuAvatar from "./NavAvatar";
import TopbarSearch, { SearchPage } from "./TopbarSearch";
import TopbarNotifications from "./TopbarNotifications";
import {
  ApplicationRole,
  ReportTypes,
  UserNotificationResponse,
  UsersService,
} from "../../api";
import { useSelector } from "react-redux";
import { isEmpty } from "../../services/Helpers";
import { Collapse, Tooltip } from "@mui/material";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import WorkOutlinedIcon from "@mui/icons-material/WorkOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import PhoneInTalkOutlinedIcon from "@mui/icons-material/PhoneInTalkOutlined";
import ContactPhoneOutlinedIcon from "@mui/icons-material/ContactPhoneOutlined";
import FolderSharedOutlinedIcon from "@mui/icons-material/FolderSharedOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import PostAddOutlinedIcon from "@mui/icons-material/PostAddOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import { usersToTrackTime } from "../../pages/Admin/LeadsManagement/shared/Constants";
import { SIDEBAR_WIDTH, SIDEBAR_WIDTH_COLLAPSED } from "./navConstants";

type NavItem = {
  name: string;
  /** Full title for the top bar / document context when `name` is abbreviated. */
  pageTitle?: string;
  link?: string;
  roles?: ApplicationRole[];
  subItems?: NavItem[];
  icon?: JSX.Element;
};

const CALLS_GROUP = "Zoom";
const RINGCENTRAL_GROUP = "RingCentral";

/**
 * Navigation IA — short labels in the sidebar; `pageTitle` for the top bar.
 * Roles/routes unchanged; Time tracking is injected as its own section at runtime.
 */
const allNavItems: NavItem[] = [
  {
    name: "Intake",
    pageTitle: "New intake",
    link: "/lead/new",
    icon: <PostAddOutlinedIcon />,
    roles: [ApplicationRole.ADMIN, ApplicationRole.STAFF],
  },
  {
    name: "Leads",
    icon: <PeopleAltOutlinedIcon />,
    roles: [
      ApplicationRole.ADMIN,
      ApplicationRole.STAFF,
      ApplicationRole.REFFERAL_STAFF,
      ApplicationRole.STAFF_ATTORNEY,
      ApplicationRole.QA,
    ],
    subItems: [
      {
        name: "All",
        pageTitle: "All leads",
        link: "/all",
        roles: [ApplicationRole.ADMIN],
      },
      {
        name: "New",
        pageTitle: "New leads",
        link: "/new-leads",
        roles: [ApplicationRole.ADMIN, ApplicationRole.STAFF, ApplicationRole.QA],
      },
      {
        name: "In Review",
        pageTitle: "Leads in review",
        link: "/approve-leads",
        roles: [
          ApplicationRole.ADMIN,
          ApplicationRole.STAFF_ATTORNEY,
          ApplicationRole.REFFERAL_STAFF,
        ],
      },
      {
        name: "Referrals",
        pageTitle: "Referral leads",
        link: "/referral-leads",
        roles: [ApplicationRole.ADMIN, ApplicationRole.REFFERAL_STAFF],
      },
      {
        name: "Active Clients",
        link: "/active-clients",
        roles: [ApplicationRole.ADMIN, ApplicationRole.STAFF],
      },
      {
        name: "Archived",
        pageTitle: "Archived leads",
        link: "/archive",
        roles: [ApplicationRole.ADMIN, ApplicationRole.QA],
      },
    ],
  },
  {
    name: "My Leads",
    link: "/attorney-leads",
    icon: <WorkOutlinedIcon />,
    roles: [ApplicationRole.ATTORNEY],
  },
  {
    name: "Complete Profile",
    link: "/attorney-info",
    icon: <EditNoteOutlinedIcon />,
    roles: [ApplicationRole.USER],
  },
  {
    name: "Attorneys",
    icon: <GavelOutlinedIcon />,
    roles: [ApplicationRole.ADMIN],
    subItems: [
      {
        name: "Directory",
        pageTitle: "Attorney directory",
        link: "/attorneys",
        roles: [ApplicationRole.ADMIN],
      },
      {
        name: "Review Schedule",
        link: "/review-assignment",
        roles: [ApplicationRole.ADMIN],
      },
    ],
  },
  {
    name: CALLS_GROUP,
    icon: <PhoneInTalkOutlinedIcon />,
    roles: [
      ApplicationRole.CLIO_ADMIN,
      ApplicationRole.STAFF,
      ApplicationRole.STAFF_ATTORNEY,
      ApplicationRole.CLIO_STAFF,
      ApplicationRole.ADMIN,
      ApplicationRole.ATTORNEY,
    ],
    subItems: [
      {
        name: "Communications",
        pageTitle: "Communications",
        link: "/zoom",
        roles: [
          ApplicationRole.CLIO_ADMIN,
          ApplicationRole.STAFF,
          ApplicationRole.STAFF_ATTORNEY,
          ApplicationRole.CLIO_STAFF,
        ],
      },
      {
        name: "Phone Recordings",
        link: "/zoom/recordings",
        roles: [
          ApplicationRole.CLIO_ADMIN,
          ApplicationRole.STAFF,
          ApplicationRole.STAFF_ATTORNEY,
          ApplicationRole.CLIO_STAFF,
          ApplicationRole.ADMIN,
          ApplicationRole.ATTORNEY,
        ],
      },
    ],
  },
  {
    name: RINGCENTRAL_GROUP,
    icon: <ContactPhoneOutlinedIcon />,
    roles: [
      ApplicationRole.CLIO_ADMIN,
      ApplicationRole.STAFF,
      ApplicationRole.STAFF_ATTORNEY,
      ApplicationRole.CLIO_STAFF,
      ApplicationRole.ADMIN,
      ApplicationRole.ATTORNEY,
    ],
    subItems: [
      {
        name: "Recordings",
        link: "/ringcentral/recordings",
        roles: [
          ApplicationRole.CLIO_ADMIN,
          ApplicationRole.STAFF,
          ApplicationRole.STAFF_ATTORNEY,
          ApplicationRole.CLIO_STAFF,
          ApplicationRole.ADMIN,
          ApplicationRole.ATTORNEY,
        ],
      },
    ],
  },
  {
    name: "Clio",
    icon: <FolderSharedOutlinedIcon />,
    roles: [
      ApplicationRole.ADMIN,
      ApplicationRole.STAFF_ATTORNEY,
      ApplicationRole.CLIO_ADMIN,
      ApplicationRole.CLIO_STAFF,
      ApplicationRole.CLIO_TASK_ASSIGNER,
    ],
    subItems: [
      {
        name: "Tasks",
        roles: [
          ApplicationRole.ADMIN,
          ApplicationRole.CLIO_ADMIN,
          ApplicationRole.CLIO_STAFF,
          ApplicationRole.CLIO_TASK_ASSIGNER,
        ],
        subItems: [
          {
            name: "Assign Tasks",
            link: "/matterInteractions",
            roles: [ApplicationRole.CLIO_ADMIN, ApplicationRole.CLIO_TASK_ASSIGNER],
          },
          {
            name: "Task Templates",
            pageTitle: "Task Templates",
            link: "/taskInteractions",
            roles: [ApplicationRole.CLIO_ADMIN],
          },
        ],
      },
      {
        name: "Folder Structure",
        pageTitle: "Matter folder structure",
        link: "/matterFolderStructure",
        roles: [
          ApplicationRole.ADMIN,
          ApplicationRole.CLIO_ADMIN,
          ApplicationRole.CLIO_STAFF,
        ],
      },
    ],
  },
  {
    name: "Reports",
    icon: <AssessmentOutlinedIcon />,
    roles: [
      ApplicationRole.ADMIN,
      ApplicationRole.CLIO_ADMIN,
      ApplicationRole.STAFF,
      ApplicationRole.STAFF_ATTORNEY,
      ApplicationRole.CLIO_STAFF,
    ],
    subItems: [
      {
        name: "Lead Reports",
        roles: [ApplicationRole.ADMIN],
        subItems: [
          {
            name: "By Staff",
            pageTitle: "Leads by staff",
            link: `/reports/leads/${ReportTypes.LEADS_STAFF}`,
            roles: [ApplicationRole.ADMIN],
          },
          {
            name: "By Attorney",
            pageTitle: "Leads by attorney",
            link: `/reports/leads/${ReportTypes.LEADS_ATTORNEY}`,
            roles: [ApplicationRole.ADMIN],
          },
          {
            name: "Intake Overview",
            link: `/reports/leads/${ReportTypes.LEADS_DATE}`,
            roles: [ApplicationRole.ADMIN],
          },
        ],
      },
      {
        name: "Clio Reports",
        roles: [
          ApplicationRole.ADMIN,
          ApplicationRole.CLIO_ADMIN,
          ApplicationRole.STAFF,
          ApplicationRole.STAFF_ATTORNEY,
          ApplicationRole.CLIO_STAFF,
        ],
        subItems: [
          {
            name: "Overview",
            pageTitle: "Staff matters overview",
            link: `/reports/clio/${ReportTypes.OVERVIEW}`,
            roles: [
              ApplicationRole.CLIO_ADMIN,
              ApplicationRole.STAFF_ATTORNEY,
              ApplicationRole.CLIO_STAFF,
            ],
          },
          {
            name: "Filing",
            pageTitle: "Filing report",
            link: "/filingReport",
            roles: [
              ApplicationRole.CLIO_ADMIN,
              ApplicationRole.CLIO_STAFF,
              ApplicationRole.STAFF_ATTORNEY,
            ],
          },
          {
            name: "Litigation Settlements",
            link: `/reports/clio/${ReportTypes.SETTLEMENT_LIT}`,
            roles: [ApplicationRole.ADMIN],
          },
          {
            name: "Pre-Lit Settlements",
            link: `/reports/clio/${ReportTypes.SETTLEMENT_PRELIT}`,
            roles: [ApplicationRole.CLIO_ADMIN],
          },
          {
            name: "KPIs",
            pageTitle: "Pre-lit KPIs",
            link: `/reports/clio/${ReportTypes.KPI}`,
            roles: [ApplicationRole.CLIO_ADMIN],
          },
          {
            name: "SOLES",
            pageTitle: "SOLES report",
            link: `/reports/clio/${ReportTypes.SOLES}`,
            roles: [ApplicationRole.CLIO_ADMIN],
          },
        ],
      },
      {
        name: "Mediation",
        pageTitle: "Mediation stages",
        link: `/mediationReport`,
        roles: [ApplicationRole.CLIO_ADMIN],
      },
      {
        name: "Litigation Funnel",
        roles: [
          ApplicationRole.CLIO_ADMIN,
          ApplicationRole.CLIO_STAFF,
          ApplicationRole.STAFF_ATTORNEY,
        ],
        subItems: [
          {
            name: "Overview",
            pageTitle: "Litigation Funnel overview",
            link: `/reports/clio/${ReportTypes.LITIGATION_FUNNEL}`,
            roles: [
              ApplicationRole.CLIO_ADMIN,
              ApplicationRole.CLIO_STAFF,
              ApplicationRole.STAFF_ATTORNEY,
            ],
          },
          {
            name: "LLO",
            pageTitle: "LLO funnel",
            link: `/lloFunnelReport`,
            roles: [
              ApplicationRole.CLIO_ADMIN,
              ApplicationRole.CLIO_STAFF,
              ApplicationRole.STAFF_ATTORNEY,
            ],
          },
          {
            name: "LLO to Form",
            pageTitle: "LLO cases to form",
            link: "/llo-cases-to-form",
            roles: [
              ApplicationRole.CLIO_ADMIN,
              ApplicationRole.CLIO_STAFF,
              ApplicationRole.STAFF_ATTORNEY,
            ],
          },
          {
            name: "Negotiations",
            pageTitle: "Negotiations report",
            link: `/negotiationsReport`,
            roles: [
              ApplicationRole.CLIO_ADMIN,
              ApplicationRole.CLIO_STAFF,
              ApplicationRole.STAFF_ATTORNEY,
            ],
          },
          {
            name: "Case Breakdown",
            pageTitle: "Case Breakdown report",
            link: `/caseBreakdownReport`,
            roles: [
              ApplicationRole.CLIO_ADMIN,
              ApplicationRole.CLIO_STAFF,
              ApplicationRole.STAFF_ATTORNEY,
            ],
          },
          {
            name: "Stages",
            pageTitle: "Litigation stages",
            link: `/litigationFunnelReport`,
            roles: [ApplicationRole.CLIO_ADMIN],
          },
        ],
      },
    ],
  },
  {
    name: "Archive",
    icon: <Inventory2OutlinedIcon />,
    roles: [ApplicationRole.ADMIN],
    subItems: [
      {
        name: "Update Assignees",
        link: "/taskAssignees",
        roles: [ApplicationRole.ADMIN],
      },
      {
        name: "Extensions",
        roles: [ApplicationRole.ADMIN],
        subItems: [
          {
            name: "Litigation & Arbitration",
            pageTitle: "Litigation & arbitration extensions",
            link: "/extensions/litigationArbitration",
            roles: [ApplicationRole.ADMIN],
          },
          {
            name: "Limited Litigation",
            pageTitle: "Limited litigation extensions",
            link: "/extensions/limitedLitigation",
            roles: [ApplicationRole.ADMIN],
          },
          {
            name: "Litigation Transition",
            pageTitle: "Litigation transition extensions",
            link: "/extensions/litigationTransition",
            roles: [ApplicationRole.ADMIN],
          },
          {
            name: "Completed & Rejected",
            pageTitle: "Completed & rejected extensions",
            link: "/extensions/archived",
            roles: [ApplicationRole.ADMIN],
          },
        ],
      },
      {
        name: "Clio Reports",
        roles: [ApplicationRole.ADMIN],
        subItems: [
          {
            name: "Extensions",
            pageTitle: "Task extensions report",
            link: `/reports/clio/${ReportTypes.EXTENSIONS}`,
            roles: [ApplicationRole.ADMIN],
          },
          {
            name: "Stage Changes",
            pageTitle: "Matter stage changes",
            link: `/reports/clio/${ReportTypes.MATTER_STAGE_CHANGE}`,
            roles: [ApplicationRole.ADMIN],
          },
          {
            name: "Assignee Changes",
            pageTitle: "Task assignee changes",
            link: `/reports/clio/${ReportTypes.TASK_ASSIGNEE_CHANGE}`,
            roles: [ApplicationRole.ADMIN],
          },
          {
            name: "Negotiation Log",
            link: `/reports/clio/${ReportTypes.NEGOTIATION_LOG}`,
            roles: [ApplicationRole.ADMIN],
          },
          {
            name: "Negotiation Results",
            pageTitle: "Negotiation outcomes",
            link: `/reports/clio/${ReportTypes.NEGOTIATION_RESULT}`,
            roles: [ApplicationRole.ADMIN],
          },
        ],
      },
    ],
  },
  {
    name: "Settings",
    icon: <SettingsOutlinedIcon />,
    roles: [ApplicationRole.ADMIN],
    subItems: [
      {
        name: "Users",
        pageTitle: "Users",
        link: "/staff-users",
        roles: [ApplicationRole.ADMIN],
      },
      {
        name: "Integrations",
        pageTitle: "Integrations",
        link: "/settings/integrations",
        roles: [ApplicationRole.ADMIN],
      },
    ],
  },
];

/** Routes outside the sidebar that still need a top-bar title. */
const FALLBACK_PAGE_TITLES: { test: RegExp; title: string }[] = [
  { test: /^\/lead\/send\//, title: "Send leads" },
  { test: /^\/lead\/[^/]+\/supplemental/, title: "Supplemental intake" },
  { test: /^\/lead\/[^/]+\/referral-case/, title: "Case status" },
  { test: /^\/lead\/[^/]+\/class/, title: "Class action intake" },
  { test: /^\/lead\/[^/]+/, title: "Lead details" },
  { test: /^\/attorney\/new/, title: "New attorney" },
  { test: /^\/attorney\/[^/]+/, title: "Attorney profile" },
  { test: /^\/staff-users\/[^/]+/, title: "User details" },
  { test: /^\/attorney-cases/, title: "Cases" },
  { test: /^\/settings$/, title: "Profile settings" },
  { test: /^\/checkout\//, title: "Checkout" },
  { test: /^\/paymentDone/, title: "Payment complete" },
  { test: /^\/generateLink/, title: "Registration link" },
  { test: /^\/zoom\/recordings/, title: "Phone Recordings" },
  { test: /^\/zoom$/, title: "Communications" },
  { test: /^\/ringcentral$/, title: "RingCentral calls" },
];

/** Nav links may carry query strings, routing only ever matches on the path. */
const toPath = (link?: string) => (link ? link.split("?")[0] : "");

const itemHoldsPath = (item: NavItem, pathname: string): boolean => {
  if (toPath(item.link) === pathname) return true;
  return item.subItems?.some((sub) => itemHoldsPath(sub, pathname)) ?? false;
};

const findPageTitle = (items: NavItem[], pathname: string): string => {
  for (const item of items) {
    if (toPath(item.link) === pathname) return item.pageTitle ?? item.name;
    if (item.subItems?.length) {
      const nested = findPageTitle(item.subItems, pathname);
      if (nested) return nested;
    }
  }
  return "";
};

const resolvePageTitle = (items: NavItem[], pathname: string): string => {
  const fromNav = findPageTitle(items, pathname);
  if (fromNav) return fromNav;
  for (const entry of FALLBACK_PAGE_TITLES) {
    if (entry.test.test(pathname)) return entry.title;
  }
  return "";
};

/** Browser tab / document title lookup against the full nav tree. */
export function getAppPageTitle(pathname: string): string {
  return resolvePageTitle(allNavItems, pathname);
}

const filterNavTree = (
  items: NavItem[],
  isAllowed: (item: NavItem) => boolean
): NavItem[] =>
  items
    .filter(isAllowed)
    .map((item) => {
      if (!item.subItems?.length) return item;
      const subItems = filterNavTree(item.subItems, isAllowed);
      return { ...item, subItems };
    })
    .filter((item) => !item.subItems || item.subItems.length > 0);

const collectSearchPages = (
  items: NavItem[],
  trail: string[] = []
): SearchPage[] => {
  const pages: SearchPage[] = [];
  for (const item of items) {
    const nextTrail = [...trail, item.name];
    if (item.link) {
      pages.push({
        title: item.pageTitle ?? item.name,
        to: toPath(item.link),
        section: trail[0],
        keywords: [...nextTrail, item.pageTitle ?? "", item.link].join(" "),
      });
    }
    if (item.subItems?.length) {
      pages.push(...collectSearchPages(item.subItems, nextTrail));
    }
  }
  return pages;
};

type NavMenuProps = {
  isDesktop: boolean;
  collapsed: boolean;
  mobileOpen: boolean;
  onCollapseToggle: () => void;
  onMobileToggle: () => void;
  onMobileClose: () => void;
};

export default function NavMenu({
  isDesktop,
  collapsed,
  mobileOpen,
  onCollapseToggle,
  onMobileToggle,
  onMobileClose,
}: NavMenuProps) {
  const [navItems, setNavItems] = useState<NavItem[]>([]);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const [notifications, setNotifications] = useState<
    UserNotificationResponse[]
  >([]);

  const location = useLocation();
  const user = useSelector((state: any) => state.user?.user);

  // Only the desktop sidebar shrinks to a rail; the mobile drawer always shows labels.
  const railMode = collapsed && isDesktop;
  const sidebarWidth = railMode ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH;

  useEffect(() => {
    if (isEmpty(user)) {
      setNavItems([]);
      setNotifications([]);
      return;
    }

    UsersService.getApiUsersNotifications().then((userNotifications) => {
      setNotifications(userNotifications ?? []);
    });

    const rolesSet = new Set(user.roles);
    const isAllowed = (item: NavItem) =>
      !item.roles || item.roles.some((role) => rolesSet.has(role));

    let items: NavItem[] = filterNavTree(allNavItems, isAllowed);

    if (usersToTrackTime.includes(user.id)) {
      const timeItem: NavItem = {
        name: "Time Tracking",
        pageTitle: "Time tracking",
        link: "/timekeeping",
        icon: <AccessTimeOutlinedIcon />,
      };
      const callsIndex = items.findIndex((item) => item.name === CALLS_GROUP);
      if (callsIndex >= 0) {
        items = [
          ...items.slice(0, callsIndex + 1),
          timeItem,
          ...items.slice(callsIndex + 1),
        ];
      } else {
        items = [...items, timeItem];
      }
    }

    setNavItems(items);
  }, [user]);


  // Keep open every ancestor group that contains the current route.
  useEffect(() => {
    const next: Record<string, boolean> = {};
    const markOpen = (items: NavItem[], parentKey = "") => {
      for (const item of items) {
        if (!item.subItems?.length) continue;
        const key = parentKey ? `${parentKey}/${item.name}` : item.name;
        if (itemHoldsPath(item, location.pathname)) {
          next[key] = true;
          markOpen(item.subItems, key);
        }
      }
    };
    markOpen(navItems);
    if (Object.keys(next).length) {
      setOpenGroups((prev) => ({ ...prev, ...next }));
    }
  }, [navItems, location.pathname]);

  const pageTitle = useMemo(
    () => resolvePageTitle(navItems, location.pathname),
    [navItems, location.pathname]
  );

  const searchPages = useMemo(() => {
    const fromNav = collectSearchPages(navItems);
    const extras: SearchPage[] = [
      {
        title: "Profile settings",
        to: "/settings",
        section: "Account",
        keywords: "profile settings account notifications payments",
      },
      {
        title: "Integrations",
        to: "/settings/integrations",
        section: "Settings",
        keywords: "integrations clio zoom ringcentral hubspot oauth webhooks",
      },
    ];
    const seen = new Set(fromNav.map((page) => page.to));
    return [
      ...fromNav,
      ...extras.filter((page) => !seen.has(page.to)),
    ];
  }, [navItems]);

  const handleNavigate = () => {
    if (!isDesktop) {
      onMobileClose();
    }
  };

  const handleNotificationsMarkedRead = () => {
    setNotifications((prev) =>
      prev.map((notification) =>
        notification.isRead ? notification : { ...notification, isRead: true }
      )
    );
  };

  const renderLeafItem = (item: NavItem, depth = 0) => {
    const active = toPath(item.link) === location.pathname;
    const nestedClass =
      depth === 0 ? "" : depth === 1 ? " nested" : " nested nested-deep";
    const button = (
      <ListItemButton
        className={`sidebar-link${nestedClass}`}
        component={Link}
        to={item.link!}
        selected={active}
        onClick={handleNavigate}
      >
        {depth === 0 && (
          <ListItemIcon className="sidebar-link__icon">
            {item.icon ?? <PeopleAltOutlinedIcon />}
          </ListItemIcon>
        )}
        {!railMode && (
          <ListItemText
            className="sidebar-link__text"
            primary={item.name}
            primaryTypographyProps={{ noWrap: true }}
          />
        )}
      </ListItemButton>
    );

    return (
      <ListItem key={item.name} disablePadding className="sidebar-item">
        {railMode ? (
          <Tooltip title={item.name} placement="right" arrow>
            {button}
          </Tooltip>
        ) : (
          button
        )}
      </ListItem>
    );
  };

  const renderGroupItem = (item: NavItem, parentKey = "", depth = 0) => {
    const groupKey = parentKey ? `${parentKey}/${item.name}` : item.name;
    const expanded = !!openGroups[groupKey];
    const holdsActive = itemHoldsPath(item, location.pathname);
    // While the group is open its highlighted child already marks the spot,
    // so the header only takes the full treatment when that child is hidden.
    const showAsSelected = holdsActive && (railMode || !expanded);
    const nestedClass =
      depth === 0 ? "" : depth === 1 ? " nested" : " nested nested-deep";

    const toggle = () => {
      // The rail has no room for sub item labels, so open it back up first.
      if (railMode) {
        onCollapseToggle();
        setOpenGroups((prev) => ({ ...prev, [groupKey]: true }));
        return;
      }
      setOpenGroups((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }));
    };

    const button = (
      <ListItemButton
        className={`sidebar-link${nestedClass}${
          holdsActive && !showAsSelected ? " holds-active" : ""
        }`}
        selected={showAsSelected}
        onClick={toggle}
      >
        {depth === 0 && (
          <ListItemIcon className="sidebar-link__icon">
            {item.icon ?? <PeopleAltOutlinedIcon />}
          </ListItemIcon>
        )}
        {!railMode && (
          <>
            <ListItemText
              className={`sidebar-link__text${
                depth > 0 ? " sidebar-link__text--section" : ""
              }`}
              primary={item.name}
              primaryTypographyProps={{ noWrap: true }}
            />
            {expanded ? (
              <ExpandLess className="sidebar-link__chevron" />
            ) : (
              <ExpandMore className="sidebar-link__chevron" />
            )}
          </>
        )}
      </ListItemButton>
    );

    return (
      <ListItem
        key={groupKey}
        disablePadding
        className={`sidebar-item sidebar-item--group${
          depth > 0 ? " sidebar-item--subgroup" : ""
        }`}
      >
        {railMode ? (
          <Tooltip title={item.name} placement="right" arrow>
            {button}
          </Tooltip>
        ) : (
          button
        )}
        {!railMode && (
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <List
              disablePadding
              className={
                depth === 0 ? "sidebar-sublist" : "sidebar-sublist sidebar-sublist--deep"
              }
            >
              {item.subItems!.map((subItem) =>
                subItem.subItems?.length
                  ? renderGroupItem(subItem, groupKey, depth + 1)
                  : renderLeafItem(subItem, depth + 1)
              )}
            </List>
          </Collapse>
        )}
      </ListItem>
    );
  };

  const sidebarContent = (
    <div className="sidebar-shell">
      <div className="sidebar-brand">
        <span className="sidebar-brand__mark">GL</span>
        {!railMode && (
          <span className="sidebar-brand__text">
            Great <strong>Lawyers</strong>
          </span>
        )}
      </div>
      <List className="sidebar-nav" disablePadding>
        {navItems.map((item) =>
          item.subItems?.length ? renderGroupItem(item) : renderLeafItem(item)
        )}
      </List>
      {!isEmpty(user) && <NavMenuAvatar railMode={railMode} />}
    </div>
  );

  return (
    <>
      <AppBar
        className="app-topbar"
        elevation={0}
        sx={{
          width: { xs: "100%", md: `calc(100% - ${sidebarWidth}px)` },
          ml: { xs: 0, md: `${sidebarWidth}px` },
        }}
      >
        <Toolbar className="app-topbar__toolbar">
          <IconButton
            className="app-topbar__toggle"
            aria-label={isDesktop ? "toggle sidebar" : "open navigation drawer"}
            edge="start"
            onClick={isDesktop ? onCollapseToggle : onMobileToggle}
          >
            {isDesktop && !collapsed ? <MenuOpenIcon /> : <MenuIcon />}
          </IconButton>

          <Typography variant="h6" className="app-topbar__title" noWrap>
            {pageTitle}
          </Typography>

          <div className="app-topbar__spacer" />

          {!isEmpty(user) && (
            <>
              <TopbarSearch pages={searchPages} roles={user.roles} />
              <TopbarNotifications
                notifications={notifications}
                onMarkedRead={handleNotificationsMarkedRead}
              />
            </>
          )}
        </Toolbar>
      </AppBar>

      <Box component="nav" className="app-sidebar" aria-label="main navigation">
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={onMobileClose}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: SIDEBAR_WIDTH,
            },
          }}
          PaperProps={{ className: "sidebar-paper" }}
        >
          {sidebarContent}
        </Drawer>
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: sidebarWidth,
            },
          }}
          PaperProps={{
            className: `sidebar-paper${railMode ? " sidebar-paper--rail" : ""}`,
          }}
        >
          {sidebarContent}
        </Drawer>
      </Box>
    </>
  );
}
